import type { Step } from "@views/Upholstery/types"

/**
 * Steps whose option lists currently come from Dato but should be overridden
 * when equivalent data exists on the matched PDM product.
 */
export const PDM_MERGE_STEP_KEYS = [
  "body",
  "rows",
  "build",
  "factory-match",
] as const

export type PdmMergeStepKey = (typeof PDM_MERGE_STEP_KEYS)[number]

/**
 * Deep-merge a Dato step with a Partial step from PDM (keeps ids/titles from Dato
 * unless the patch replaces them explicitly).
 */
export function mergeStepOverlay(base: Step, patch: Partial<Step>): Step {
  const mergedSubs = patch.substeps != null ? patch.substeps : base.substeps

  const mergedCat =
    patch.category != null
      ? {
          ...base.category,
          ...patch.category,
          optionValues:
            patch.category.optionValues ?? base.category?.optionValues ?? [],
        }
      : base.category

  return {
    ...base,
    ...patch,
    compatibility:
      patch.compatibility !== undefined
        ? patch.compatibility
        : base.compatibility ?? null,
    category: mergedCat,
    substeps: mergedSubs,
    factoryMatches:
      patch.factoryMatches !== undefined
        ? patch.factoryMatches
        : base.factoryMatches,
  }
}

/** Apply per-step overlays only for keys listed in {@link PDM_MERGE_STEP_KEYS}. */
export function applyPdmOverlaysToDatoSteps(
  datoSteps: Step[],
  overlays: Partial<Record<PdmMergeStepKey, Partial<Step>>>
): Step[] {
  const keys = new Set<string>(PDM_MERGE_STEP_KEYS)

  return datoSteps.map((step) => {
    if (!keys.has(step.key)) {
      return step
    }

    const patch = overlays[step.key as PdmMergeStepKey]
    if (!patch || Object.keys(patch).length === 0) {
      return step
    }

    return mergeStepOverlay(step, patch)
  })
}
